import { Message } from "@/i18n/Message";
import { Component, Suspense, type ReactNode } from "react";
import { isHostedPwaDistribution, requestPwaUpdate } from "../app/clientContract";
import { holdInlineStalePrompt, isChunkLoadError, noteChunkLoadFailure } from "../app/staleBuild";
import LoadingView from "./LoadingView";

/** A delayed screen must not blank the surrounding shell or live application. */
export default class DeferredView extends Component<
  { children: ReactNode; fallback?: ReactNode },
  { failed: boolean; staleBuild: boolean }
> {
  state = { failed: false, staleBuild: false };

  static getDerivedStateFromError(error: unknown) {
    return { failed: true, staleBuild: isChunkLoadError(error) };
  }

  private releasePrompt: (() => void) | null = null;

  componentDidCatch(error: unknown) {
    if (!isChunkLoadError(error)) return;
    noteChunkLoadFailure(error);
    this.releasePrompt ??= holdInlineStalePrompt();
  }

  componentWillUnmount() {
    this.releasePrompt?.();
  }

  render() {
    if (this.state.failed) {
      // React.lazy keeps the rejected import, so only a page load recovers.
      const reload = () => {
        if (isHostedPwaDistribution()) void requestPwaUpdate();
        else window.location.reload();
      };
      return (
        <div role="alert" className="deferred-view-error">
          {this.state.staleBuild
            ? <Message message={"Ato has been updated. Reload to open this view."} />
            : <Message message={"This view couldn’t be loaded."} />}{" "}
          <button type="button" className="ato-button ato-button--secondary" onClick={reload}><Message message={"Reload page"} /></button>
        </div>
      );
    }
    return (
      <Suspense fallback={this.props.fallback ?? <LoadingView />}>
        {this.props.children}
      </Suspense>
    );
  }
}
